import { User } from '../types';
import { isBusinessOwner } from './roleUtils';

export type NavigationItem = {
  id: string;
  label: string;
  icon: string; // For backward compatibility, can be emoji or Bootstrap icon class
  iconClass?: string; // Bootstrap icon class (e.g., 'bi bi-bar-chart-steps')
  iconColor?: string; // Icon color class (e.g., 'icon-blue', 'icon-pink')
  permission?: string;
};

export type NavigationSection = {
  title?: string;
  items: NavigationItem[];
};

export const getBusinessDashboardNavigation = (user: User, businessName?: string): NavigationSection[] => {
  const sections: NavigationSection[] = [
    {
      items: [
        { id: 'settings', label: 'Settings', icon: '🔧', iconClass: 'bi bi-sliders', iconColor: 'text-gray-600', permission: 'manage_settings' },
      ]
    },
    {
      title: 'BUSINESS',
      items: [
        { id: 'appointments', label: 'Appointments', icon: '📅', iconClass: 'bi bi-calendar-event', iconColor: 'text-[#0B4778]', permission: 'view_appointments' },
        { id: 'quote-requests', label: 'Quote Requests', icon: '📋', iconClass: 'bi bi-file-earmark-text', iconColor: 'text-[#0B4778]', permission: 'view_appointments' },
        { id: 'invoices', label: 'Invoices', icon: '🧾', iconClass: 'bi bi-receipt', iconColor: 'text-blue-600', permission: 'view_appointments' },
        { id: 'customers', label: 'Customers', icon: '👨‍👩‍👧‍👦', iconClass: 'bi bi-people', iconColor: 'text-purple-600' },
        { id: 'services', label: 'Services', icon: '🛠️', iconClass: 'bi bi-list-ul', iconColor: 'text-teal-600', permission: 'view_services' },
        { id: 'closures', label: 'Closures', icon: '🚫', iconClass: 'bi bi-calendar-x', iconColor: 'text-red-600', permission: 'manage_settings' },
        // Gift Cards hidden from sidebar for now
        // { id: 'giftcards', label: 'Gift Cards', icon: '🎁', iconClass: 'bi bi-gift', iconColor: 'text-pink-600', permission: 'manage_gift_cards' },
      ]
    }
  ];

  // Only show TEAM section for business owners
  if (isBusinessOwner(user)) {
    sections.push({
      title: 'TEAM',
      items: [
        { id: 'roles', label: 'Roles and Access', icon: '🔐', iconClass: 'bi bi-shield-lock', iconColor: 'text-purple-600', permission: 'manage_roles' },
        { id: 'professionals', label: 'Personnel', icon: '👥', iconClass: 'bi bi-people', iconColor: 'text-purple-600', permission: 'manage_professionals' },
      ]
    });
  }

  // Only show SITE section for business owners
  if (isBusinessOwner(user)) {
    sections.push({
      title: 'SITE',
      items: [
        { id: 'site-config', label: 'Site Configuration', icon: '⚙️', iconClass: 'bi bi-gear', iconColor: 'text-gray-600', permission: 'manage_settings' },
        { id: 'testimonials', label: 'Testimonials', icon: '⭐', iconClass: 'bi bi-star', iconColor: 'text-amber-500', permission: 'manage_testimonials' },
      ]
    });
  }

  // Add "MY WORK" section for users who can perform services
  if (user?.canPerformServices) {
    sections.push({
      title: 'MY WORK',
      items: [
        { id: 'my-appointments', label: 'My Appointments', icon: '📋', iconClass: 'bi bi-calendar2-week', iconColor: 'text-[#0B4778]' },
        { id: 'my-services', label: 'My Services', icon: '🛠️', iconClass: 'bi bi-list-ul', iconColor: 'text-teal-600' },
        { id: 'my-work-schedule', label: 'My Work Schedule', icon: '⏰', iconClass: 'bi bi-alarm', iconColor: 'text-pink-600' },
        { id: 'my-customer-reviews', label: 'My Reviews', icon: '⭐', iconClass: 'bi bi-star', iconColor: 'text-green-600' }
      ]
    });
  }

  // Add "AACORE PLATFORM" section
  sections.push({
    title: 'AACORE PLATFORM',
    items: [
      { id: 'home', label: 'AACore Home', icon: '🏠', iconClass: 'bi bi-house-door', iconColor: 'text-[#0B4778]' },
      { id: 'my-website', label: 'My Website', icon: '🌐', iconClass: 'bi bi-globe2', iconColor: 'text-[#0B4778]' },
    ]
  });

  return sections;
};

export const getCustomerDashboardNavigation = (): NavigationItem[] => {
  return [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'appointments', label: 'My Appointments', icon: '📅' },
    // Gift Cards hidden from customer nav for now
    // { id: 'gift-cards', label: 'My Gift Cards', icon: '🎁' },
    { id: 'settings', label: 'Account Settings', icon: '⚙️' },
    { id: 'notifications', label: 'Notifications', icon: '🔔' },
  ];
};

// Helper function to determine if user should see business dashboard
export const shouldShowBusinessDashboard = (user: User): boolean => {
  // If user can perform services, they should see business dashboard
  if (user?.canPerformServices) {
    return true;
  }

  if (!user.roles || user.roles.length === 0) {
    return false;
  }

  const activeRoles = user.roles.filter(role => role.isActive);
  
  if (activeRoles.length === 0) {
    return false;
  }

  // First, check if any role has an explicit dashboardType
  for (const role of activeRoles) {
    if (role.dashboardType) {
      switch (role.dashboardType) {
        case 'business':
          return true;
        case 'customer':
          return false;
      }
    }
  }

  // If no explicit dashboardType, use permission-based logic
  // Collect all permissions from all active roles
  const allPermissions = new Set<string>();
  
  for (const role of activeRoles) {
    if (role.permissions && role.permissions.length > 0) {
      role.permissions.forEach(permission => allPermissions.add(permission));
    }
  }

  // If no permissions at all, default to customer dashboard
  if (allPermissions.size === 0) {
    return false;
  }

  const permissions = Array.from(allPermissions);

  // Define permission patterns that indicate which dashboard to use
  const businessManagementPermissions = [
    'manage_services', 'manage_roles', 'manage_professionals', 
    'manage_settings', 'manage_branding', 'view_reports', 'manage_testimonials',
    'manage_gift_cards', 'manage_appointments'
  ];

  const professionalPermissions = [
    'manage_schedule', 'view_own_appointments', 'manage_own_services',
    'manage_availability', 'view_schedule', 'can_perform_services'
  ];

  // Check if user has business management capabilities
  const hasBusinessPermissions = businessManagementPermissions.some(permission => 
    permissions.includes(permission)
  );

  // Check if user has professional capabilities
  const hasProfessionalPermissions = professionalPermissions.some(permission => 
    permissions.includes(permission)
  );

  // All users with any business or professional permissions go to business dashboard
  return hasBusinessPermissions || hasProfessionalPermissions;
};

export const getDashboardTitle = (user: User): string => {
  return shouldShowBusinessDashboard(user) ? 'Business Dashboard' : 'Customer Dashboard';
};

export const getUserNavigation = (user: User): NavigationSection[] => {
  if (shouldShowBusinessDashboard(user)) {
    return getBusinessDashboardNavigation(user);
  } else {
    const customerItems = getCustomerDashboardNavigation();
    return [{ items: customerItems }];
  }
}; 